import { createReducer, on } from '@ngrx/store';
import { InitialState, ReviewFeatureState } from './review.state';
import { aiAssistedRepliesAdapter, aiDefaultLanguagesAdapter, aiSettingsAdapter, replyTemplatesAdapter, reviewsAdapter, reviewTranslationsAdapter } from './review-adapters';
import { AiAssistedRepliesPerReview } from '../../../models/ai-assisted-replies-per-review';
import { AiReplyActions, AiSettingsActions, ReplyTemplatesActions, ReviewActions, ReviewsActions, ReviewSyncActions } from './review-actions';
import { cloneDeep } from 'lodash';


export const reviewReducer = createReducer(InitialState,
    on(ReviewsActions.getReviewsSuccess, (state, { reviewsResponse }): ReviewFeatureState => ({
      ...state,
      reviews: reviewsAdapter.setAll(reviewsResponse.reviews, state.reviews),
      from: reviewsResponse.from,
      to: reviewsResponse.to,
      totalNumber: reviewsResponse.totalNumber,
    })),
    on(ReviewsActions.removeReview, (state, { id }): ReviewFeatureState => ({
      ...state,
      reviews: reviewsAdapter.removeOne(id, state.reviews),
    })),
    on(ReviewActions.saveReviewSuccess, (state, { review }): ReviewFeatureState =>
        ({
          ...state,
          reviews: reviewsAdapter.upsertOne(review, state.reviews),
        })),
    on(ReviewSyncActions.syncReviewFilters, (state, { reviewFilters }): ReviewFeatureState => ({
      ...state,
      filters: reviewFilters,
    })),
    on(ReviewSyncActions.syncReviewPaginatorData, (state, {
      pageIndex,
      pageSize,
      totalNumber,
    }): ReviewFeatureState => ({
      ...state,
      pageIndex,
      pageSize,
      totalNumber: totalNumber ? totalNumber : state.totalNumber,
    })),
    on(ReplyTemplatesActions.getReplyTemplatesSuccess, (state, { replyTemplates }): ReviewFeatureState => ({
      ...state,
      replyTemplates: replyTemplatesAdapter.setAll(replyTemplates, state.replyTemplates),
    })),
    on(ReplyTemplatesActions.upsertReplyTemplate, (state, { replyTemplate }): ReviewFeatureState => ({
      ...state,
      replyTemplates: replyTemplatesAdapter.upsertOne(replyTemplate, state.replyTemplates),
    })),
    on(ReplyTemplatesActions.deleteReplyTemplate, (state, { id }): ReviewFeatureState => ({
      ...state,
      replyTemplates: replyTemplatesAdapter.removeOne(id, state.replyTemplates),
    })),
    on(AiReplyActions.getAiAssistedReplySuccess, (state, {
      aiAssistedReply,
      id,
    }): ReviewFeatureState => {
      const updatedReplies = state.aiAssistedReplies.entities[id]?.replies ?
          [...state.aiAssistedReplies.entities[id]?.replies] : [];
      updatedReplies.push(aiAssistedReply);

      const repliesPerReview: AiAssistedRepliesPerReview = {
        id,
        replies: updatedReplies,
      };

      return {
        ...state,
        aiAssistedReplies: aiAssistedRepliesAdapter.upsertOne(repliesPerReview, state.aiAssistedReplies),
      };
    }),
    on(AiSettingsActions.getAiSettingsSuccess, (state, { aiSettings }): ReviewFeatureState => ({
      ...state,
      aiSettings: aiSettingsAdapter.setAll(aiSettings, state.aiSettings),
    })),
    on(AiSettingsActions.getAiSettingsError, (state): ReviewFeatureState => ({
      ...state,
    })),
    on(AiSettingsActions.saveAiSettingsSuccess, AiSettingsActions.updateDefaultLanguageSuccess,
        (state, { aiSettings }): ReviewFeatureState => ({
          ...state,
          aiSettings: aiSettingsAdapter.upsertOne(aiSettings, state.aiSettings),
        })),
    on(AiSettingsActions.deleteAiSettingsSuccess, (state, { id }): ReviewFeatureState => ({
      ...state,
      aiSettings: aiSettingsAdapter.removeOne(id, state.aiSettings),
    })),
    on(AiSettingsActions.unSelectAiSettings, (state, { aiSettings }): ReviewFeatureState => {
      const updatedAiSettings = cloneDeep(state.aiSettings.entities[aiSettings.id]);
      if (updatedAiSettings) {
        updatedAiSettings.selected = false;
        return {
          ...state,
          aiSettings: aiSettingsAdapter.upsertOne(updatedAiSettings, state.aiSettings),
        };
      } else {
        return state;
      }
    }),
    on(AiSettingsActions.unSelectAiSettingsSuccess, (state, { aiSettings }): ReviewFeatureState => ({
      ...state,
      aiSettings: aiSettingsAdapter.upsertOne(aiSettings, state.aiSettings),
    })),
    on(AiSettingsActions.selectAiSettings, (state, { aiSettings }): ReviewFeatureState => {
      const updatedAiSettings = cloneDeep(state.aiSettings.entities[aiSettings.id]);
      if (updatedAiSettings) {
        updatedAiSettings.selected = true;
        return {
          ...state,
          aiSettings: aiSettingsAdapter.upsertOne(updatedAiSettings, state.aiSettings),
        };
      } else {
        return state;
      }
    }),
    on(AiSettingsActions.selectAiSettingsSuccess, (state, { aiSettings }): ReviewFeatureState => ({
      ...state,
      aiSettings: aiSettingsAdapter.upsertOne(aiSettings, state.aiSettings),
    })),
    on(AiSettingsActions.getDefaultLanguagesSuccess, (state, { defaultLanguages }): ReviewFeatureState => ({
      ...state,
      defaultLanguages: aiDefaultLanguagesAdapter.setAll(defaultLanguages, state.defaultLanguages),
    })),
    on(AiSettingsActions.saveAiSettingsSuccess, AiSettingsActions.updateReplyStyleSuccess,
        (state, { aiSettings }): ReviewFeatureState => ({
          ...state,
          aiSettings: aiSettingsAdapter.upsertOne(aiSettings, state.aiSettings),
        })),
    on(ReviewActions.translateReviewSuccess, (state, { reviewTranslation }): ReviewFeatureState => ({
      ...state,
      reviewTranslations: reviewTranslationsAdapter.upsertOne(reviewTranslation, state.reviewTranslations),
    })),
    // Purges
    on(ReviewsActions.garbageCollectStaleData, (state, {
      translationsToRemove,
      repliesToRemove,
    }): ReviewFeatureState => ({
      ...state,
      reviewTranslations: translationsToRemove === undefined
          ? reviewTranslationsAdapter.removeAll(state.reviewTranslations)
          : reviewTranslationsAdapter.removeMany(translationsToRemove, state.reviewTranslations),
      aiAssistedReplies: repliesToRemove === undefined
          ? aiAssistedRepliesAdapter.removeAll(state.aiAssistedReplies)
          : aiAssistedRepliesAdapter.removeMany(repliesToRemove, state.aiAssistedReplies),
    })),
);
